/**
 * Business logic for scrolling to sections
 */

/**
 * Calculates the navbar height by finding the sticky navbar element
 * 
 * @returns The height of the navbar in pixels, or 80 as default
 */
export function getNavbarHeight(): number {
  if (typeof window === 'undefined') return 80;
  
  const allDivs = document.querySelectorAll('div');
  let navbar: HTMLElement | null = null;
  
  for (const div of Array.from(allDivs)) {
    const classes = div.className;
    if (classes.includes('sticky') && classes.includes('top-0') && classes.includes('z-')) {
      navbar = div as HTMLElement;
      break;
    }
  }
  
  return navbar ? navbar.offsetHeight : 80;
}

/**
 * Scrolls to a section element with offset for navbar
 * 
 * @param element - The HTML element to scroll to
 * @param offset - Additional offset in pixels (default: 20)
 */
export function scrollToElement(element: HTMLElement, offset: number = 20): void {
  if (!element) return;
  
  // Use requestAnimationFrame to ensure DOM is ready
  requestAnimationFrame(() => {
    const navbarHeight = getNavbarHeight();
    
    // Get current scroll position (use modern API with fallback)
    const currentScrollY = window.scrollY || (window as any).pageYOffset || 0;
    
    // Get element position relative to document
    const rect = element.getBoundingClientRect();
    const elementTop = rect.top + currentScrollY;
    
    // Calculate target scroll position accounting for navbar and offset
    const targetScroll = elementTop - navbarHeight - offset;
    
    // Scroll to the position (single scroll; no follow-up to avoid "locking" user to section)
    window.scrollTo({
      top: Math.max(0, targetScroll),
      behavior: 'smooth'
    });
  });
}

/**
 * Scrolls to a section by ID with offset for navbar
 * 
 * @param sectionId - The ID of the section to scroll to
 * @param offset - Additional offset in pixels (default: 20)
 */
export function scrollToSection(sectionId: string, offset: number = 20): void {
  if (typeof window === 'undefined') return;
  
  // Try multiple selectors to find the element
  let element = document.querySelector(`#${sectionId}`) as HTMLElement;
  if (!element) {
    // Fallback: try data-section attribute
    element = document.querySelector(`[data-section="${sectionId}"]`) as HTMLElement;
  }
  
  if (element) {
    // Update URL hash
    const hash = `#${sectionId}`;
    if (window.location.hash !== hash) {
      window.history.pushState(null, '', hash);
    }
    
    scrollToElement(element, offset);
  } else {
    console.warn(`Section element not found: ${sectionId}`);
    // Last resort: try scrolling to hash directly
    window.location.hash = sectionId;
  }
}

